/**
 * OTLP/JSON TRACE protocol vocabulary -- the wire enums and the flow-status mapping the span adapter emits.
 *
 * HAND-BUILT, ZERO `@opentelemetry/*` PACKAGES (operator decision). The SDK's only contribution at this seam would be
 * these three enums and a serializer; adding a dependency tree to obtain them buys nothing and puts a second definition
 * of the wire shape in the tree. Any future OTel dep needs a named runtime consumer that is not this file.
 *
 * SEPARATE FROM `const/observability-otel-attrs.ts` on purpose. That leaf is the CLAUDE CODE HARNESS attribute
 * vocabulary and its docstring binds it to the harness emitter contract verbatim; the OTel-standard RESOURCE keys below
 * are emitted by THIS repo about ITS OWN services. Folding them in would also widen
 * `QUAL-OTEL-ATTR-KEY-RAW-LITERAL-001`'s runtime-derived alternation over every harness reader, manufacturing
 * violations on a vocabulary those readers never carry.
 */

import { FLOW_RUN_FAILURE_STATUSES, FLOW_RUN_SUCCESS_STATUSES, FLOW_STATUS } from '@federa/const/flow-engine-status';
import { fnv1a64Hex } from '@federa/const/fnv1a';
import { FEDERA_ATTRS } from '@federa/const/telemetry-federa-attrs';
import { flowSpanPayloadPreview, runMeasureSpanAttributes } from '@federa/const/flow-engine-span-attributes';
import { flowRunSpanName, flowStepSpanName } from '@federa/const/flow-engine-span-names';
import { SUBFLOW_STEP_ROW_OUTPUT } from '@federa/const/flow-subflow-step-row';
import { finiteNumberOrNull, isFiniteNumber } from '@federa/const/number-guards';
import { asRecordOrNull, recordStringOr, rowString } from '@federa/const/record-guards';
import type { FlowStepResultRecord, FlowRunRecord } from '@federa/types/api-flow';
import type {
	OtlpResourceIdentity,
	OtlpSpan,
	OtlpSpanAttribute,
	OtlpSpanKind,
	OtlpSpanStatus,
	OtlpSpanStatusCode,
} from '@federa/types/telemetry-otlp-span';
import type { OtlpStringAttribute } from '@federa/types/telemetry-otlp';

/**
 * OTLP `Span.kind`. A flow run and its steps are INTERNAL work: no remote peer is implied by either, so INTERNAL is the
 * only kind this repo emits. The full wire mapping (0 UNSPECIFIED through 5 CONSUMER) is preserved on `OtlpSpanKind`
 * (`types/telemetry-otlp-span.ts`); a member is re-added here in the change that adds its emitter, never ahead of it.
 *
 * A PROPOSAL WAS MEASURED AGAINST THIS LEAF AND REFUTED, 2026-08-21, and the refutation is recorded because the
 * proposal reads as obviously right. The operator's 45-span trace carried FIFTEEN spans at 0.00ms -- `route-logging`,
 * `route-duplicate`, `route-structured-tokens`, `route-junction`, `route-prepared`, `prepare`, `respond`,
 * `shape-side-effects`, `success-response`, `check-vectorize` -- all of them engine STEP spans, emitted per GRAPH NODE
 * rather than per unit of work, and the proposed cure was "a span KIND the trace view can collapse, NOT deletion"
 * (deleting them loses graph visibility). Two readings close it:
 *
 *  1. THE LANE THAT SHOWED THEM HAS NO KIND KNOB. Those spans reached that view through the platform tracer
 *     (`tools/telemetry/worker-native-tracer.ts`) over `cloudflare:workers` `tracing.startActiveSpan`, whose scope
 *     entry is `(name: string, run: (span: unknown) => unknown) => unknown` (`SpanScopeFn`,
 *     `utils/telemetry/worker-span-primitives.ts`). Name and callback: there is no options argument and therefore no
 *     kind to set. The early-beta surface would have to grow one first.
 *  2. THE LANE THAT HAS ONE ALREADY EMITS THE COLLAPSIBLE VALUE. This record is the repo's only span-kind emitter, and
 *     every span the OTLP leg exports -- root and step alike -- is already INTERNAL, which is precisely the kind a
 *     viewer collapses by default. There is nothing here to change.
 *
 * AND THE DISCRIMINATOR THE VIEW ACTUALLY NEEDS ALREADY EXISTS, in the NAME rather than the kind: every step span has
 * been `step.<flowId>.<stepId>` since 2026-08-16 (`flowStepSpanName`, `const/flow-engine-span-names.ts`), so "collapse
 * the graph-node tier" is a PREFIX MATCH available to any reader today, and a strictly sharper one than a kind --
 * `INTERNAL` cannot separate a graph node from any other internal work, while the prefix names exactly that tier.
 *
 * MODULE-LOCAL since 2026-09-05, when `otlpRootSpan` joined `otlpStepSpan` here and the record mapper stopped
 * importing it: both readers are now in-leaf, and a two-way census over the tracked tree found no other. The one
 * remaining outside mention is a docstring pointer in `const/flow-engine-span-names.ts` naming where the derivation
 * lives, which stays exactly true of a module-local declaration.
 */
const OTLP_SPAN_KIND = {
	INTERNAL: 1,
} as const satisfies Record<string, OtlpSpanKind>;

/** OTLP `Status.code`. UNSET is NOT a synonym for OK -- it means the producer took no position, which is what a still-RUNNING or deliberately CANCELED run warrants AND, per the spec, what an ordinary SUCCESS warrants too: `OK` is an explicit application override, so nothing this repo emits sets it. The member stays declared because it is the wire enum, and a reader parsing an inbound third-party export will meet it. */
export const OTLP_SPAN_STATUS_CODE = {
	UNSET: 0,
	OK: 1,
	ERROR: 2,
} as const satisfies Record<string, OtlpSpanStatusCode>;

/** OTel-STANDARD resource keys (semconv spelling, not a `federa.` extension), so a generic collector groups this fleet's traces without a custom mapping. The RECORD stays module-private: `otlpResourceAttributes` below is its only reader. A door-side consumer reading one of these keys off an inbound export re-exports THAT MEMBER in the change that adds that reader, never ahead of it -- {@link OTEL_DEPLOYMENT_ENVIRONMENT_ATTR} took that step first, {@link OTEL_SERVICE_NAME_ATTR} followed 2026-09-01 for the warehouse `ServiceName` bag promotion, and `service.version` still has no outside reader. */
const OTEL_RESOURCE_ATTRS = {
	SERVICE_NAME: 'service.name',
	DEPLOYMENT_ENVIRONMENT: 'deployment.environment.name',
	/**
	 * Which build emitted the span, so a regression can be bounded to a deploy instead of to a date range. CONFIG-SUPPLIED
	 * like its two siblings, and NEVER derived from `WORKER_DEPLOY_NAMES` (recorded direction) -- for the same reason the
	 * service name is not: the same tree runs these flows from a CLI, the session server and a daemon child under no
	 * deploy name at all, so a table-derived version would label a local run with whatever the fleet last shipped.
	 */
	SERVICE_VERSION: 'service.version',
} as const;

/**
 * The semconv `deployment.environment.name` key, re-exported as a SINGLE MEMBER because a reader outside this module
 * now exists: the `deployment_environment` row in `TELEMETRY.LOGS_COLUMNS` (`const/telemetry.ts`) promotes this
 * key out of the warehouse attributes bag, and the parity rule's sibling test pins that row's `sourceKeys` spelling
 * against THIS byte so a semconv respelling reds instead of drifting.
 *
 * NARROW ON PURPOSE, and the narrowness is the recorded direction above rather than taste -- exporting the whole
 * record would ship `service.name` and `service.version` to a reader that does not exist, which is the "never ahead of
 * it" half of that direction. Keeping the record itself private is also what keeps the seam suites'
 * `tools/observability/__tests__/otel-seam-trace-roundtrip.test.ts` / `otel-seam-privacy-cardinality.test.ts` note --
 * "the producer's `OTEL_RESOURCE_ATTRS` is module-private, so the literal is the contract" -- true rather than stale.
 *
 * NOT A `FEDERA_ATTRS` MEMBER, and it must not become one. That registry is this repo's OWN vocabulary; the
 * foreclosure is recorded on {@link OTEL_SPAN_ERROR_ATTRS} for the identical situation (`error.type`), and it applies
 * unchanged here: enrolling a semconv key there would put a spelling nobody in this repo owns under a rule whose job
 * is governing keys nobody else defines.
 *
 * @reader-rule QUAL-DEPLOYMENT-ENV-ALIAS-SSOT-BYPASS-001
 */
export const OTEL_DEPLOYMENT_ENVIRONMENT_ATTR = OTEL_RESOURCE_ATTRS.DEPLOYMENT_ENVIRONMENT;

/**
 * The SECOND member to take the reader-arrival step this leaf's contract prescribes (2026-09-01): the warehouse
 * transform promotes `service.name` out of the attribute bag into the `ServiceName` column
 * (`TELEMETRY.LOGS_COLUMNS`), and the column-parity suite's fail-closed bag-key membership needs the key declared
 * by an SSOT rather than respelled -- exactly the reader whose arrival the docstring above says unlocks a member.
 * Same foreclosure as its sibling: this is a semconv spelling, never a `federa.` extension.
 */
export const OTEL_SERVICE_NAME_ATTR = OTEL_RESOURCE_ATTRS.SERVICE_NAME;

/**
 * OTel-STANDARD SPAN keys, same reasoning as the resource block above and the same module-private shape: the exported
 * shaper is the only reader, so the byte has one home.
 *
 * `error.type` is the semconv key for a failure
 * (https://opentelemetry.io/docs/specs/semconv/general/recording-errors/). It is deliberately NOT a `FEDERA_ATTRS`
 * member: that registry holds this repo's OWN vocabulary, and enrolling a standard key there would put a semconv
 * spelling under a rule whose whole job is governing keys nobody else defines -- and would make a generic collector's
 * built-in error handling look like a fleet extension.
 */
const OTEL_SPAN_ERROR_ATTRS = {
	ERROR_TYPE: 'error.type',
} as const;

/** Nanoseconds per millisecond, as the decimal-string suffix an ms timestamp gains to become a UnixNano. A nanosecond epoch exceeds `Number.MAX_SAFE_INTEGER`, so the conversion is STRING concatenation and never arithmetic. */
const UNIX_NANO_SUFFIX = '000000';

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a millisecond epoch as the decimal-string nanosecond epoch OTLP
 * demands. String-built rather than `ms * 1e6`, which silently loses precision above 2^53 and would put a rounded
 * timestamp on the wire with nothing reporting it.
 *
 * A NON-FINITE INPUT YIELDS `'0'`, AND `'0'` IS NOT AN "UNSET" SENTINEL -- stated plainly because the opposite reading
 * is the tempting one and it is wrong at the consumer. OTLP/JSON has no unset representation for a required span
 * timestamp, and this fleet's own reader treats a zero as a real instant: `recordTimestampMs`
 * (`utils/observability/record-timestamp-ms.ts`) divides the nano value and hands back epoch-0, which partitions
 * as 1970. So a `'0'` here is a DEFECT SIGNAL a reader can see, never a benign omission. The function stays TOTAL
 * rather than throwing because dropping a whole run's trace over one bad timestamp is the worse failure -- and the
 * arm is unreachable from the span builder anyway, whose inputs are `FlowRunRecord.createdAt` / `durationMs`, both
 * required numbers the engine always stamps.
 */
export function unixNanoString(milliseconds: number): string {
	if (!Number.isFinite(milliseconds)) return '0';
	return `${Math.trunc(milliseconds)}${UNIX_NANO_SUFFIX}`;
}

/** Members of `FLOW_RUN_SUCCESS_STATUSES`, as a set for the mapping below. DERIVED, so a third success terminal maps itself. */
const SUCCESS_TERMINALS: ReadonlySet<string> = new Set<string>(Object.values(FLOW_RUN_SUCCESS_STATUSES));

/** Members of `FLOW_RUN_FAILURE_STATUSES`, same derivation. */
const FAILURE_TERMINALS: ReadonlySet<string> = new Set<string>(Object.values(FLOW_RUN_FAILURE_STATUSES));

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the flow-run status -> OTLP span status mapping.
 *
 * DERIVED from the two adjudicated terminal SETS rather than re-enumerating `FLOW_RUN_STATUS`, so the three-way OTLP
 * vocabulary inherits the repo's own success/failure adjudication instead of forking it -- including the deliberate
 * NON-PARTITION those sets form: `canceled` is in neither, and `running` is not terminal at all, so both land on UNSET.
 * That is the correct OTLP reading (the producer took no position), NOT a defaulting accident, and it is why this is a
 * mapping rather than a boolean.
 *
 * SUCCESS MAPS TO UNSET, NOT OK (corrected 2026-08-14), and the two are NOT synonyms on the wire. The trace spec is
 * explicit: "Span Status Code MUST be left unset if the instrumented operation has ended without any errors", and `Ok`
 * is reserved for an explicit APPLICATION override -- a deliberate "I am overruling anything that looked like a failure
 * here". Stamping OK on every succeeded run spent that signal on the default case, so a span a caller genuinely wanted
 * to force-green was indistinguishable from one that merely finished, and the ordinary success case advertised an
 * override no one made. The mapping stays three-way; only the success ARM moved. Failure terminals are unchanged at
 * ERROR, which is where all the information was anyway.
 *
 * READER-SIDE ONLY. It never changes what a run record CARRIES -- `record.status` rides every transport verbatim, which
 * the run-record status pass-through parity suite pins.
 *
 * NO CROSS-FILE IMPORTER, and that is the REGISTERED state rather than dead code (`orphan export`, arrived on the
 * QUAL-DEAD-EXPORT-003 day-one queue when the span adapter's last out-of-leaf caller folded in at d9413a543b6). The
 * consumer is ACTIVE `QUAL-OTLP-RUN-STATUS-MAP-INLINE-001`, which convicts a hand-rolled flow-run-status -> OTLP-status
 * map and names THIS symbol as the import to make -- its `SSOT_CALL_TOKEN` carries the name in literal position and its
 * `message`, `name` and `fix` name it as the call to write. That is a prescribed-import edge no import graph can see,
 * and dropping the `export` keyword the dead-export `fix` directs would leave that rule prescribing a symbol no
 * offender can reach -- a remediation-completeness break dressed as a cleanup.
 *
 * THE TAG BELOW IS THAT EDGE IN MACHINE-CHECKABLE FORM, preferred over a `RULE_PRESCRIBED_CANONICAL_WRITERS` entry for
 * the reason that register's own `run-id-shape.ts` note records: the claim is RESOLVED against the live tree on every
 * run, so a retired or renamed prescriber makes the row return LOUDER, where a path-keyed register entry would instead
 * exclude this whole leaf's twenty-odd other exports from the rule and hide every future row in it.
 *
 * @reader-rule QUAL-OTLP-RUN-STATUS-MAP-INLINE-001
 */
export function otlpStatusCodeForRunStatus(status: string): OtlpSpanStatusCode {
	if (SUCCESS_TERMINALS.has(status)) return OTLP_SPAN_STATUS_CODE.UNSET;
	if (FAILURE_TERMINALS.has(status)) return OTLP_SPAN_STATUS_CODE.ERROR;
	return OTLP_SPAN_STATUS_CODE.UNSET;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the standard `error.type` attribute for a FAILING span, and NOTHING
 * for any other status.
 *
 * ONLY ON THE ERROR ARM, which is the spec's own instruction ("successful operations SHOULD NOT set `error.type`") and
 * not a size optimisation: a key present on every span makes its value the discriminator rather than its presence, so a
 * reader filtering `error.type EXISTS` -- the query every generic error dashboard is built from -- would match the whole
 * corpus. `otlpStringAttributes` then drops a blank reason for the same reason it drops any blank, so a failure whose
 * producer supplied no reason emits the status code alone rather than an empty type.
 *
 * The VALUE is the run or step failure REASON the record already carries. The typed `reasonCode` slug is preferred where
 * one exists precisely because semconv wants a low-cardinality class here; free error text is the honest fallback but is
 * higher-cardinality than the spec intends, which is recorded rather than hidden.
 *
 * MODULE-LOCAL since 2026-09-05, same ground as {@link OTLP_SPAN_KIND}: both span tiers now call it from inside this
 * leaf and the record mapper no longer imports it. Two-way census over the tracked tree: no mention outside this leaf.
 */
function otlpErrorTypeAttributes(code: OtlpSpanStatusCode, reason: unknown): OtlpSpanAttribute[] {
	if (code !== OTLP_SPAN_STATUS_CODE.ERROR) return [];
	return otlpStringAttributes([[OTEL_SPAN_ERROR_ATTRS.ERROR_TYPE, reason]]);
}

/** Trace-id width in hex characters; a collector rejects any other width. */
const TRACE_ID_HEX_CHARS = 32;

/** Everything that is not a lowercase hex digit, for normalising a UUID into a trace id. */
const NON_HEX_CHARS_REGEX = /[^0-9a-f]/g;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the trace id for a run.
 *
 * DERIVED FROM THE RUN ID, never minted, and this is the field that makes the export correlatable: a run id that is
 * already a UUID normalises to exactly the 32 hex chars a trace id needs, so an operator holding a `wf_runs.run_id`
 * can paste it into a trace search. A run id of any other shape is hashed into two 64-bit halves instead. The
 * all-zero trace id is INVALID per the spec, so it falls through to the hash rather than being emitted.
 */
export function otlpTraceIdFor(runId: string): string {
	const normalized = runId.toLowerCase().replace(NON_HEX_CHARS_REGEX, '');
	if (normalized.length === TRACE_ID_HEX_CHARS && /[1-9a-f]/.test(normalized)) return normalized;
	return `${fnv1a64Hex(runId)}${fnv1a64Hex(`${runId}:trace`)}`;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the span id for one node within a run, or for the run's ROOT span
 * when `stepId` is omitted. Deterministic, so a re-export of the same record produces the same span rather than a
 * duplicate the collector cannot dedupe.
 */
export function otlpSpanIdFor(runId: string, stepId?: string): string {
	return fnv1a64Hex(stepId === undefined ? `${runId}:root` : `${runId}:node:${stepId}`);
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): a log-derived timestamp as UnixNano, or `defaultMs` when the source carried no usable number. */
function unixNanoOr(value: unknown, defaultMs: number): string {
	return unixNanoString(finiteNumberOrNull(value) ?? defaultMs);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the OTLP KeyValue wire form for a list of string attributes.
 *
 * A pair whose value is absent or blank is DROPPED, never emitted empty: on the wire, `key` present with `''` is
 * indistinguishable from a real empty value, so a reader cannot tell "the producer had nothing" from "the field was
 * empty" -- the same reader-must-not-invent-a-cause failure an unexplained empty step list produces.
 */
export function otlpStringAttributes(pairs: readonly (readonly [string, unknown])[]): OtlpSpanAttribute[] {
	const out: OtlpSpanAttribute[] = [];
	for (const [key, value] of pairs) {
		if (typeof value !== 'string' || value === '') continue;
		out.push(otlpStringAttribute(key, value));
	}
	return out;
}

/** The OTLP KeyValue wire form for ONE string attribute, emitted as given -- the caller has already decided it rides.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): `utils/hooks/post-tool-use-failure-telemetry-emit.ts` value-binds
 *  it, and the same-file {@link otlpStringAttributes} composes it. */
export function otlpStringAttribute(key: string, value: string): OtlpStringAttribute {
	return { key, value: { stringValue: value } };
}

/** The OTLP KeyValue wire form for ONE boolean attribute -- `false` rides as a real value, never dropped. The engine
 *  span-bag shaper below and the browser producer's attribute arm (`const/browser-telemetry-attrs.ts`) both emit it.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the boolean arm of this leaf's KeyValue shapers. */
export function otlpBoolAttribute(key: string, value: boolean): OtlpSpanAttribute {
	return { key, value: { boolValue: value } };
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the OTLP KeyValue wire form for an ENGINE span-attribute bag -- the
 * `FlowEngineSpanAttributes` shape the worker native tracer consumes directly -- so a derivation written once for that
 * tracer (`runMeasureSpanAttributes`, `const/flow-engine-span-attributes.ts`) reaches the OTLP leg without a second
 * spelling of which keys ride and how. Strings take the string form (blank dropped, as {@link otlpStringAttributes}),
 * finite numbers the int64 form (an engine measurement is a millisecond or a count, never a fraction), booleans the
 * boolean form. A key is never emitted twice: the bag is a record.
 *
 * MODULE-LOCAL since 2026-09-05, on the same ground as {@link OTLP_SPAN_KIND}: its only reader was the record mapper,
 * which now takes the whole root span from {@link otlpRootSpan} instead. Two-way census over the tracked tree: no
 * mention outside this leaf at all.
 */
function otlpAttributesFromSpanBag(bag: Readonly<Record<string, string | number | boolean>>): OtlpSpanAttribute[] {
	const out: OtlpSpanAttribute[] = [];
	for (const [key, value] of Object.entries(bag)) {
		if (typeof value === 'boolean') out.push(otlpBoolAttribute(key, value));
		else if (typeof value === 'number') out.push(...otlpIntAttributes([[key, value]]));
		else out.push(...otlpStringAttributes([[key, value]]));
	}
	return out;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the OTLP KeyValue wire form for a list of INTEGER attributes, the
 * counterpart to `otlpStringAttributes` above. Homed beside that sibling rather than in the LOG severity leaf, so a
 * span-signal consumer never reaches into a log leaf for a generic wire shaper.
 *
 * `intValue` is a DECIMAL STRING on the OTLP/JSON wire because the field is an int64, so the conversion is
 * `String(...)` and never a bare number -- a numeric literal there is a different wire type a strict collector rejects.
 * A non-finite or non-numeric value is DROPPED for the same reason its string sibling drops a blank: on the wire a key
 * present with a placeholder is indistinguishable from a real measurement of that value.
 */
function otlpIntAttributes(pairs: readonly (readonly [string, unknown])[]): OtlpSpanAttribute[] {
	const out: OtlpSpanAttribute[] = [];
	for (const [key, value] of pairs) {
		if (!isFiniteNumber(value)) continue;
		out.push({ key, value: { intValue: String(Math.trunc(value)) } });
	}
	return out;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the child-span TRUNCATION DISCLOSURE pair for a run's root span.
 *
 * Lives here rather than in the mapper because the CONTRACT is the pair, not the mapping: both keys are emitted on
 * every export, including when nothing was truncated and including a zero-step run. A disclosure emitted only on the
 * truncated arm would make its ABSENCE ambiguous between an untruncated run and a producer too old to say -- and a
 * reader comparing the two numbers is the whole point, since `MAX_CHILD_SPANS` child spans could otherwise mean either
 * a run with exactly that many steps or one that lost its tail.
 *
 * MODULE-LOCAL since 2026-09-05. The "lives here rather than in the mapper" reading above is unchanged and is now
 * simply complete: the root span it discloses for is here too, so the pair contract has ONE reader and it is in-leaf.
 * Two-way census over the tracked tree: no mention outside this leaf.
 */
function otlpRunStepsAttributes(total: number, exported: number): OtlpSpanAttribute[] {
	return otlpIntAttributes([
		[FEDERA_ATTRS.RUN_STEPS_TOTAL.name, total],
		[FEDERA_ATTRS.RUN_STEPS_EXPORTED.name, exported],
	]);
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): the Resource attribute list for one export, over the semconv keys above. */
export function otlpResourceAttributes(identity: OtlpResourceIdentity): OtlpSpanAttribute[] {
	return otlpStringAttributes([
		[OTEL_RESOURCE_ATTRS.SERVICE_NAME, identity.serviceName],
		[OTEL_RESOURCE_ATTRS.DEPLOYMENT_ENVIRONMENT, identity.deploymentEnvironment],
		// No fallback default, unlike its two siblings: `otlpStringAttributes` DROPS a blank, and an absent
		// `service.version` is honest where a placeholder ('unknown', '0.0.0') is a value a reader would filter and
		// group by. The siblings carry defaults because a span with no service name is unattributable; a span with no
		// version is merely less specific.
		[OTEL_RESOURCE_ATTRS.SERVICE_VERSION, identity.serviceVersion],
	]);
}

/** Wire bound on one span's `Status.message`. A SPAN-FIELD bound, which is why it sits with the protocol rather than with the transport contract: it holds whatever endpoint the span is exported to. */
const STATUS_MESSAGE_MAX_CHARS = 1_000;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): ONE status shaper for both span tiers.
 *
 * `message` rides the ERROR arm ONLY. The OTLP spec leaves a message on a non-error status undefined, and a collector
 * that surfaces it turns a successful run's stale error text into a phantom failure in the trace UI -- so an OK span
 * built from a record whose `error` field was never cleared must still emit no message.
 *
 * MODULE-LOCAL since 2026-09-06, on the same ground and in the same shape as `OTLP_SPAN_KIND` above and
 * `otlpRunOutputPreview` below: `otlpRootSpan` joined `otlpStepSpan` here on 2026-09-05 and the OTLP record mapper
 * stopped importing this shaper, so both readers are the two in-leaf callers and a two-way census over the tracked
 * tree found no third. The `export` keyword is dropped rather than the declaration -- the marker stays because the
 * classification is about the CODE living in a const/ leaf, which un-exporting does not change.
 *
 * QUAL-DEAD-EXPORT-003 DID NOT REPORT THIS ROW, and the acquittal is a MATCHER defect rather than a consumer. The
 * only cross-file occurrence is a pair of FIXTURE STRINGS inside
 * `tools/code-scanner/compliance/rules/pure/__tests__/QUAL-OTLP-RUN-STATUS-MAP-INLINE-001.test.ts` -- array elements
 * of a synthetic source body, not an import of this leaf. `hasSymbolConsumer` still credits them: `commentMaskedBody`
 * preserves string literals by design (the specifier a real consumer writes lives inside quotes), and
 * `pkgPathConsumesKey` then conjoins a QUOTED-PATH test with a whole-file `\bname\b` test, so a quoted specifier on one
 * line and the symbol name on any other line satisfy both halves without an import edge existing. That made
 * `testConsumed` true, which handed the row to the suite-pinned acquittal (`const/suite-pinned-export.ts`) whose
 * premise -- a suite READS the decision -- is false here. Reported to the rule's owning lane 2026-09-06; the demotion
 * below is the site cure and does not depend on the matcher repair.
 */
function otlpSpanStatus(code: OtlpSpanStatusCode, errorText: unknown): OtlpSpanStatus {
	const message = typeof errorText === 'string' ? errorText.slice(0, STATUS_MESSAGE_MAX_CHARS) : '';
	return code === OTLP_SPAN_STATUS_CODE.ERROR && message !== '' ? { code, message } : { code };
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the run's output PREVIEW off the record's bounded JSON snapshot.
 *
 * The snapshot is PARSED BACK so the preview's own projection (`redactTelemetryPayload`) and its character cap apply to
 * the VALUE, exactly as the native tracer applies them to the settled envelope's output, rather than to a string of a
 * string. A record with no snapshot (`null`) emits no key -- absence is the honest reading -- and an unparseable
 * snapshot previews as the raw text, which the cap still bounds.
 */
function otlpRunOutputPreview(outputSnapshot: string | null): string | null {
	if (outputSnapshot === null) return null;
	try {
		return flowSpanPayloadPreview(JSON.parse(outputSnapshot));
	} catch {
		return flowSpanPayloadPreview(outputSnapshot);
	}
}

/** The run's identity attribute pairs -- its id and its flow -- which the root span and every step span both carry
 *  first, so a warehouse query joins the two tiers on the same keys. */
function otlpRunIdentityPairs(record: FlowRunRecord): readonly (readonly [string, unknown])[] {
	return [
		[FEDERA_ATTRS.RUN_ID.name, record.runId],
		[FEDERA_ATTRS.FLOW_NAME.name, record.flowName],
	];
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the run's ROOT span. Named for the flow, never for the run --
 * `flow.run.id` is declared `unbounded`, and a span name carrying it mints one name per run.
 *
 * IT LIVES BESIDE {@link otlpStepSpan} RATHER THAN IN THE RECORD MAPPER (`utils/telemetry/otlp-span-build.ts`), and the
 * move (2026-09-05) closed a layer split of ONE decision rather than merely a line budget. The two span TIERS of a flow
 * run are one wire contract -- same trace id, same `otlpSpanIdFor` parenting, same status mapping, same payload-preview
 * projection -- and the step half had been here since the leaf was minted while the run half sat a layer up, so the
 * pair could be widened on one side only. That asymmetry was already recorded as a rule ({@link otlpRunStepsAttributes}
 * "lives here rather than in the mapper because the CONTRACT is the pair, not the mapping") and this is the same
 * reading applied to the span the pair rides on. Every collaborator it needs is already this leaf's own or a `const/`
 * import, so nothing about the mapper's `utils/` home was load-bearing; what stays there is the ENVELOPE assembly the
 * sanctioned-assembler rule governs.
 *
 * ROOT OF THIS RUN, NOT ALWAYS ROOT OF THE TRACE. It carries a `parentSpanId` exactly when the run was entered across a
 * hop that sent a `traceparent`, in which case the parent is the CALLER'S span, staged at the entry seam and admitted at
 * the record choke point. That is not a parent RECORD -- the engine still emits exactly one record per top-level run --
 * it is this run's place in a trace another process started, and without it the run's spans join the right trace while
 * hanging beside the caller instead of beneath it, which reads as two unrelated roots.
 *
 * An unseeded run carries no `parentSpanId` at all (the key is absent, not null): a CLI run, a scheduled run and any
 * untraced entry genuinely ARE trace roots, and those honest exclusions are unchanged by this seam.
 */
export function otlpRootSpan(record: FlowRunRecord, stepsExported: number): OtlpSpan {
	const statusCode = otlpStatusCodeForRunStatus(record.status);
	const engineAttributes: OtlpSpanAttribute[] = [
		...otlpStringAttributes([
			...otlpRunIdentityPairs(record),
			// The terminal status, off the record's own column -- the same `FLOW_RUN_STATUS` member the native
			// tracer stamps at settle, so the warehouse's success buckets see this lane (header: measured absent).
			[FEDERA_ATTRS.RUN_STATUS.name, record.status],
			[FEDERA_ATTRS.TRIGGER_SOURCE.name, record.triggerSource],
			[FEDERA_ATTRS.RUN_FIRST_FAILED_STEP.name, record.firstFailedStep],
			[FEDERA_ATTRS.RUN_REASON_CODE.name, record.reasonCode],
			// The run's payload legs as PREVIEWS, through the one projection both producers share. The output rides
			// off the bounded snapshot the record already carries (its parsed form, so the preview's own projection
			// and cap apply once rather than to a string of a string); a record with no snapshot emits no key.
			[FEDERA_ATTRS.RUN_INPUT.name, flowSpanPayloadPreview(record.input)],
			[FEDERA_ATTRS.RUN_OUTPUT.name, otlpRunOutputPreview(record.outputSnapshot)],
		]),
		// The cycle-time quartet, derived where the native tracer derives it (`runMeasureSpanAttributes`).
		...otlpAttributesFromSpanBag(
			runMeasureSpanAttributes({
				durationMs: record.durationMs,
				declaredCeilingMs: record.declaredCeilingMs,
				outputSchemaViolations: record.outputSchemaViolations,
				stepOutputMemberMisses: record.stepOutputMemberMisses,
			}),
		),
		// The truncation disclosure; the always-both contract lives with the shaper.
		...otlpRunStepsAttributes(record.stepResults.length, stepsExported),
		// The standard `error.type`, on the failing arm ONLY. The TYPED reason slug is preferred over the free error
		// text precisely because semconv wants a low-cardinality class here; the text is the fallback when a producer
		// classified nothing, and the shaper drops the pair entirely when neither exists.
		...otlpErrorTypeAttributes(statusCode, record.reasonCode ?? record.error),
	];
	// THE EVENT-METRIC CHANNEL ON THIS LANE (kaizen d6cc639a, 2026-09-04): the bag the record carries
	// (`FlowRunRecord.eventAttributes`, the same `runEventMetricAttributes` composition the native tracer stamps), so an
	// event-share metric reads the node lane's runs as it reads an isolate's. THE ENGINE'S KEYS WIN: a declared key
	// already stamped above is dropped rather than appended, the array form of the object-spread ordering the native
	// lane relies on -- a producing flow must never be able to displace the identity, status or measure keys.
	const stamped = new Set(engineAttributes.map((attribute) => attribute.key));
	const declaredAttributes = otlpAttributesFromSpanBag(record.eventAttributes ?? {}).filter((attribute) => !stamped.has(attribute.key));
	return {
		traceId: record.traceId,
		spanId: otlpSpanIdFor(record.runId),
		...(record.parentSpanId === undefined ? {} : { parentSpanId: record.parentSpanId }),
		name: flowRunSpanName(record.flowName),
		kind: OTLP_SPAN_KIND.INTERNAL,
		startTimeUnixNano: unixNanoString(record.createdAt),
		endTimeUnixNano: unixNanoString(record.createdAt + record.durationMs),
		attributes: [...engineAttributes, ...declaredAttributes],
		status: otlpSpanStatus(statusCode, record.error),
	};
}

/** The step id a step span is named with when its row carries none -- a placeholder that reads as one, so a defective
 *  row is visible as `step.<flow>.unreadable-step` rather than masquerading as the run. */
const OTLP_UNREADABLE_STEP_ID = 'unreadable-step';

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): one STEP span, NAMED `<flowName>/<stepId>` (the flow name alone when
 * the row carries no step id). Both halves are graph vocabulary and therefore low-cardinality; the run id rides an
 * ATTRIBUTE, never the name. THIS SPELLING IS THIS LEG'S OWN and is NOT `flowStepSpanName`'s
 * `step.<flowId>.<stepId>` -- recorded rather than reconciled, because the divergence is load-bearing downstream: the
 * span reader's prefix test cannot see a span named this way, so its `flow.step.status` attribute arm is the only thing
 * that admits one (the parentage read in `tools/flow-data/flow-run-span-map.ts`). Converging the two names is a
 * WIRE change, so it retires that arm rather than being free.
 *
 * A NON-FAILED step is UNSET rather than OK (corrected 2026-08-14), for the reason {@link otlpStatusCodeForRunStatus}
 * states at length: the spec reserves `Ok` for an explicit application override, so a producer that stamps it on every
 * success has spent the signal. The step tier maps the separate `FLOW_STATUS` vocabulary rather than `FLOW_RUN_STATUS`,
 * so the branch stays inline here (and is the case `QUAL-OTLP-RUN-STATUS-MAP-INLINE-001` explicitly does not govern) --
 * but the two tiers now agree on what success looks like on the wire, which they did not before.
 *
 * It lives beside the other span shapers rather than in the RECORD mapper (`utils/telemetry/otlp-span-build.ts`): it
 * shapes wire vocabulary out of already-mapped row fields, which is exactly this leaf's job. Its RUN-tier twin
 * ({@link otlpRootSpan}) joined it here 2026-09-05 on that same reading, so the two tiers of one wire contract can no
 * longer be widened on one side only.
 */
export function otlpStepSpan(record: FlowRunRecord, row: FlowStepResultRecord): OtlpSpan {
	// `FlowStepResultRecord.stepId` is declared `unknown`, so this is an untyped-field read: the shared
	// `recordStringOr` body owns it (QUAL-EXTRACT-STRING-INPUT-SSOT-001), and it is already this leaf's import.
	const stepId = recordStringOr(row, 'stepId', '');
	const startedMs = typeof row.startedAt === 'number' ? row.startedAt : record.createdAt;
	const statusCode = row.status === FLOW_STATUS.FAILED ? OTLP_SPAN_STATUS_CODE.ERROR : OTLP_SPAN_STATUS_CODE.UNSET;
	return {
		traceId: record.traceId,
		spanId: otlpSpanIdFor(record.runId, stepId),
		parentSpanId: otlpSpanIdFor(record.runId),
		// THE SPAN-NAME SSOT, since 2026-09-01: `step.<flowId>.<stepId>`, the same name the worker native tracer opens
		// (`flowStepSpanName`). This leg spelled `<flow>/<step>` privately until then, so the two producers' step spans
		// grouped into different buckets in the warehouse. A row with no readable step id keeps a NAMED placeholder
		// rather than the run's own name -- naming it as the run would count it as a second run in every rollup.
		name: flowStepSpanName({ flowId: record.flowName, stepId: stepId === '' ? OTLP_UNREADABLE_STEP_ID : stepId }),
		kind: OTLP_SPAN_KIND.INTERNAL,
		startTimeUnixNano: unixNanoOr(row.startedAt, record.createdAt),
		endTimeUnixNano: unixNanoOr(row.completedAt, startedMs + (row.durationMs ?? 0)),
		attributes: [
			...otlpStringAttributes([
				...otlpRunIdentityPairs(record),
				[FEDERA_ATTRS.STEP_ID.name, stepId],
				[FEDERA_ATTRS.STEP_STATUS.name, row.status],
				[FEDERA_ATTRS.SUBFLOW_ID.name, rowString(asRecordOrNull(row.output), SUBFLOW_STEP_ROW_OUTPUT.SUBFLOW_ID)],
				// THE STEP'S PAYLOAD LEGS, ADDED 2026-08-28, and their absence until then was a silent data-loss hazard
				// rather than a gap in detail. These two attributes are what `wf_node_results.input_json` /
				// `output_json` become once that stream is retired, and the WORKER lane has emitted them since
				// 2026-08-14 (`tools/flow-engine/executor-step-dispatch.ts`) -- but this CLI/Node lane, which is the ONLY
				// producer the flow-run door ever had, never did. Retiring the stream without this line would have
				// dropped every local and CLI run's step input and output with no error at any layer, because a missing
				// span attribute is just an absent key.
				//
				// `flowSpanPayloadPreview` is the SAME helper the worker lane calls, which is the whole reason it is a
				// shared leaf -- its own docstring says "one helper, both sites, no second posture", written against
				// exactly this parity failure. The projection and the 2,000-char cap therefore match the worker lane's
				// by construction rather than by a second implementation that could drift.
				[FEDERA_ATTRS.STEP_INPUT.name, flowSpanPayloadPreview(row.input)],
				[FEDERA_ATTRS.STEP_OUTPUT.name, flowSpanPayloadPreview(row.output)],
				// THE REMOTE-BINDING REJECTION PAIR (2026-09-02): the row carries the member only when the task seam
				// classified the failure as the wrangler remote-binding proxy's rejection, and `otlpStringAttributes` drops
				// the blank pair on every other row -- so the node lane's step span names the binding and the proxy
				// reference id exactly where the worker lane's does (`stepSpanOutcomeAttributes`), from the same record.
				[FEDERA_ATTRS.REMOTE_BINDING.name, row.remoteBindingRejection?.binding ?? undefined],
				[FEDERA_ATTRS.REMOTE_BINDING_REFERENCE.name, row.remoteBindingRejection?.referenceId],
			]),
			// The standard `error.type`, on the failing arm ONLY. The step tier has no typed reason slug, so the row's own
			// error text is the honest value -- higher-cardinality than semconv intends, and recorded as such at the shaper.
			...otlpErrorTypeAttributes(statusCode, row.error),
		],
		status: otlpSpanStatus(statusCode, row.error),
	};
}
